import React, { useState, useContext, useLayoutEffect, useEffect } from "react";
import ApiRole from "../../../app/api/admin/role/ApiRole";
import ApiPermission from "../../../app/api/admin/permission/ApiPermission";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import InputWrapper from "../../components/input/__wrapper";
import InputText from "../../components/inputs/text";
import InputCheckbox from "../../components/inputs/checkbox";
import Loader from "../../components/loader";
import "./styles.css";

// Подписи для группировки прав по сущности/действию — сами права в БД без подписи
// (slug вида 'admin.{entity}.{action}'), UI просто оформляет их читаемо.
const ENTITY_LABELS = {
    'dashboard': 'Дашборд',
    'nodes': 'Ноды',
    'devices': 'Устройства',
    'device-types': 'Тарифы',
    'users': 'Пользователи',
    'roles': 'Роли',
    'scripts': 'Скрипты',
    'profile': 'Профиль',
};

const ACTION_LABELS = {
    'index': 'Список',
    'show': 'Просмотр',
    'create': 'Создание',
    'edit': 'Редактирование',
    'destroy': 'Удаление',
};

// slug 'admin.nodes.create' -> {entity: 'nodes', action: 'create'}
function parseSlug(slug) {
    const parts = slug.split('.');
    return { entity: parts[1] ?? slug, action: parts[2] ?? '' };
}

// Карточка роли: show / edit / create. permission_ids — чекбоксы, сгруппированные по сущности.
export default function RoleShow({ ...other }) {
    return <Page ApiModel={ApiRole} withDestroyOnShow={true} {...other}>
        <RoleShowContent/>
    </Page>;
}

function RoleShowContent() {
    const contextContent = useContext(ContextContent);

    const [_id, set_id] = useState('');
    const [_slug, set_slug] = useState('');
    const [_label, set_label] = useState('');
    const [_permissionIds, set_permissionIds] = useState([]);
    const [_permissions, set_permissions] = useState(null); // null — каталог прав ещё грузится

    useEffect(() => {
        (async () => {
            const api = ApiPermission.Index();
            const data = await api.getResponse();
            set_permissions(data ?? []);
        })();
    }, []);

    const togglePermission = (permissionId, checked) => {
        set_permissionIds((prev) => checked ? [...prev, permissionId] : prev.filter((id) => id !== permissionId));
    };

    contextContent._dataToServer.current = {
        slug: _slug,
        label: _label,
        permission_ids: _permissionIds,
    };
    contextContent._dataRequiredToServer.current = {
        slug: _slug,
    };

    useLayoutEffect(() => {
        contextContent._handleSetData.current = (data) => {
            set_id(data.id);
            set_slug(data.slug ?? '');
            set_label(data.label ?? '');
            set_permissionIds(data.permission_ids ?? []);
            contextContent.setTitle(`Роль: ${data.slug}`);
        };
        if (contextContent.isCreate) contextContent.setTitle('Новая роль');
        contextContent.doLoad();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const readOnly = !contextContent.isCreateOrEdit;
    const errors = contextContent.errors ?? {};

    // Группировка плоского списка прав по сущности (entity -> [{id, action, slug}]),
    // порядок сущностей — по ENTITY_LABELS, действий внутри — по ACTION_LABELS.
    const groups = {};
    (_permissions ?? []).forEach((permission) => {
        const { entity, action } = parseSlug(permission.slug);
        if (!groups[entity]) groups[entity] = [];
        groups[entity].push({ ...permission, action });
    });
    const entityOrder = Object.keys(groups).sort((a, b) => {
        const ia = Object.keys(ENTITY_LABELS).indexOf(a);
        const ib = Object.keys(ENTITY_LABELS).indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
    const actionOrder = Object.keys(ACTION_LABELS);

    return <>
        {!contextContent.isCreate ?
            <InputWrapper>
                <InputText className="input_wf" value={_id} readOnly={true}>id</InputText>
            </InputWrapper> : null}

        <InputWrapper>
            <InputText className="input_wf" value={_slug} readOnly={readOnly} errors={errors.slug}
                placeholder="напр. moderator" onChange={(e) => set_slug(e.target.value)}>Слаг</InputText>
        </InputWrapper>
        <InputWrapper>
            <InputText className="input_wf" value={_label} readOnly={readOnly} errors={errors.label}
                placeholder="напр. Модератор" onChange={(e) => set_label(e.target.value)}>Название</InputText>
        </InputWrapper>

        <div className="role-permissions">
            <div className="role-permissions__title">Права</div>
            {/* Каталог прав — отдельный запрос от карточки роли; пока он идёт, сетка чекбоксов
                пуста и роль выглядит как «прав не заведено вообще». */}
            {_permissions === null ? <Loader text="Загрузка прав…"/> : null}
            {entityOrder.map((entity) => (
                <div key={entity} className="role-permissions__row">
                    <div className="role-permissions__entity">{ENTITY_LABELS[entity] || entity}</div>
                    <div className="role-permissions__actions">
                        {groups[entity]
                            .sort((a, b) => actionOrder.indexOf(a.action) - actionOrder.indexOf(b.action))
                            .map((permission) => (
                                <InputCheckbox key={permission.id} value={_permissionIds.includes(permission.id)}
                                    readOnly={readOnly}
                                    onChange={(e) => togglePermission(permission.id, e.target.checked)}>
                                    {ACTION_LABELS[permission.action] || permission.action}
                                </InputCheckbox>
                            ))}
                    </div>
                </div>
            ))}
        </div>
    </>;
}
